import React, { useEffect, useMemo, useRef, useState } from "react";

export const ACTION_ITEM_CATEGORY_ADD_PLACEHOLDER = "+ Add category…";
export const ACTION_ITEM_CATEGORY_MAX_LENGTH = 40;

/**
 * Category picker with catalog options and an inline add field at the top of the menu.
 *
 * @example
 * <ActionItemCategoryControl
 *   value="Work"
 *   options={["Work", "Other"]}
 *   onChange={(label) => saveCategory(label)}
 * />
 * // Parent should persist new labels to performer settings when onChange fires.
 */
const ActionItemCategoryControl = ({
  value = "",
  options = [],
  onChange,
  disabled = false,
  className = "",
}) => {
  const rootRef = useRef(null);
  const addInputRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [draft, setDraft] = useState("");

  const selected = String(value || "").trim();
  const labels = useMemo(
    () =>
      (Array.isArray(options) ? options : [])
        .map((label) => String(label || "").trim())
        .filter(Boolean),
    [options]
  );
  const optionLabels = useMemo(() => {
    if (
      selected &&
      !labels.some((label) => label.toLowerCase() === selected.toLowerCase())
    ) {
      return [selected, ...labels];
    }
    return labels;
  }, [labels, selected]);

  useEffect(() => {
    if (!menuOpen) {
      setDraft("");
    }
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen || !addInputRef.current) return;
    addInputRef.current.focus();
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  const toggleMenu = (event) => {
    event.stopPropagation();
    if (disabled) return;
    setMenuOpen((open) => !open);
  };

  const selectOption = (label) => {
    const next = String(label || "").trim();
    if (!next || next === selected || !onChange) return;
    onChange(next);
    closeMenu();
  };

  const commitNewCategory = () => {
    const next = String(draft || "").trim().slice(0, ACTION_ITEM_CATEGORY_MAX_LENGTH);
    if (!next || !onChange) return;
    if (next.toLowerCase() === selected.toLowerCase()) {
      setDraft("");
      return;
    }
    onChange(next);
    closeMenu();
  };

  const handleAddKeyDown = (event) => {
    event.stopPropagation();
    if (event.key === "Enter") {
      event.preventDefault();
      commitNewCategory();
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setDraft("");
      closeMenu();
    }
  };

  const rootClass = ["action-item-category-wrap", className].filter(Boolean).join(" ");
  const triggerClass = [
    "action-item-category-trigger",
    menuOpen ? " action-item-category-trigger--open" : "",
  ].join("");

  return (
    <div className={rootClass} ref={rootRef}>
      <button
        type="button"
        className={triggerClass}
        disabled={disabled}
        onClick={toggleMenu}
        aria-haspopup="listbox"
        aria-expanded={menuOpen}
        aria-label="Category"
      >
        <span className="action-item-category-trigger__label">{selected || "Select category"}</span>
        <span className="action-item-category-trigger__chevron" aria-hidden="true">
          ▾
        </span>
      </button>

      {menuOpen ? (
        <div className="action-item-category-menu" role="listbox" aria-label="Category options">
          <div className="action-item-category-menu__add-form">
            <input
              ref={addInputRef}
              type="text"
              className="action-item-category-menu__add-input"
              value={draft}
              placeholder={ACTION_ITEM_CATEGORY_ADD_PLACEHOLDER}
              maxLength={ACTION_ITEM_CATEGORY_MAX_LENGTH}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={handleAddKeyDown}
              onBlur={commitNewCategory}
              onClick={(event) => event.stopPropagation()}
              aria-label="Add category"
            />
          </div>

          {optionLabels.length > 0 ? (
            <>
              <div className="action-item-category-menu__divider" />
              <div className="action-item-category-menu__options">
                {optionLabels.map((label) => {
                  const isActive = label.toLowerCase() === selected.toLowerCase();
                  return (
                    <button
                      key={label}
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      className={`action-item-category-menu__option${
                        isActive ? " action-item-category-menu__option--active" : ""
                      }`}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={(event) => {
                        event.stopPropagation();
                        selectOption(label);
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default ActionItemCategoryControl;
